"use client";

import Image from "next/image";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/shadcn/ui/accordion";
import { PortableText } from "@portabletext/react";
import { customRichTextComponent } from "@/sanity/sanityPortableText";

import { PageEventSchema } from "@/sanity/pages/PageEventTemplate";
import { PageKryptoDaySchema } from "@/sanity/pages/PageKryptoDayTemplate";

import gridGBimg from "@/public/lp2/faq/grid-bg.svg";
import blurBGimg from "@/public/lp2/faq/blur-bg.svg";

export default function FAQ({
  data,
}: {
  data: PageEventSchema | PageKryptoDaySchema;
}) {
  return (
    <section className="relative w-full lg:pt-0 z-[1] pt-[6rem] overflow-clip">
      {/* Backgrounds */}
      <Image
        className="absolute z-[-2] top-0 lg:top-[2rem] w-full lg:w-1/2 left-[50%] translate-x-[-50%] pointer-events-none"
        alt="Grid background"
        src={gridGBimg}
      />
      <Image
        className="faq-section-blur absolute z-[-3] top-[4rem] lg:top-[2rem] pointer-events-none lg:w-full left-[50%] translate-x-[-50%]"
        alt="Blur background"
        src={blurBGimg}
      />
      {/* End backgrounds */}
      <div className="relative mx-auto container-lp z-[1]">
        <div className="flex flex-col justify-end items-center w-full mb-[4rem] lg:h-[30rem] lg:mb-[6rem]">
          {data?.faqTexts && (
            <PortableText
              value={data?.faqTexts ?? []}
              components={customRichTextComponent({
                block: {
                  h2: ({ children }) => (
                    <h2 className="gradient-title !text-white/80 text-[5rem] lg:text-[7rem] font-[600] tracking-[-0.2rem] lg:tracking-[-0.28rem] leading-[120%] lg:leading-[140%] text-center my-[1rem]">
                      {children}
                    </h2>
                  ),
                  h6: ({ children }) => (
                    <div className="mx-auto font-bold uppercase bg-white bg-opacity-10 rounded-full border border-white border-opacity-20 w-fit px-[3rem] py-[1.5rem] leading-[2rem] text-[2rem] backdrop-blur-[4rem]">
                      {children}
                    </div>
                  ),
                  normal: ({ children }) => (
                    <p className="text-white text-opacity-75">{children}</p>
                  ),
                },
              })}
            />
          )}
        </div>

        <Accordion
          type="single"
          defaultValue="item-0"
          collapsible
          className="w-full lg:bg-black/70"
        >
          {data?.faqItems?.map((faq, index) => (
            <AccordionItem
              key={index}
              value={`item-${index}`}
              className="border-none faq-items rounded-[1.5rem] px-[4rem] py-[1rem] mb-[2rem] bg-[#ffffff0d] lg:py-[2rem]"
            >
              <AccordionTrigger className="pt-4 text-left text-white text-[2.25rem] font-[400] leading-[3.375rem] opacity-85">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="text-white text-[2.25rem] font-[400] leading-[150%] opacity-60">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}
